import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Row, Col, Spin, Empty } from 'antd';
import { useParams } from 'react-router-dom';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import CategoryNav from '../../components/CategoryNav/CategoryNav';
import MobileCategoryNav from '../../components/MobileCategoryNav/MobileCategoryNav';
import AppCard from '../../components/AppCard/AppCard';
import Ad from '../../components/Ad/Ad';
import { apiService } from '../../services/api';
import { CustomListAppItem, CustomListDetailResponse } from '../../types';
import { constants } from '../../config/constants';
import './List.css';

const { Content } = Layout;

const List: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [listDetail, setListDetail] = useState<CustomListDetailResponse | null>(null);
  const [apps, setApps] = useState<CustomListAppItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: constants.pagination.defaultPageSize,
    total: 0,
  });
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [adPositions, setAdPositions] = useState<number[]>([]);
  const gamesMainRef = useRef<HTMLElement>(null);
  const isMobileRef = useRef<boolean>(false);

  // 通过用户代理判断是否为移动端设备
  const isMobileDevice = useCallback(() => {
    if (isMobileRef.current !== undefined && typeof window !== 'undefined') {
      return isMobileRef.current;
    }
    const userAgent = navigator.userAgent || navigator.vendor || '';
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobile = mobileRegex.test(userAgent);
    isMobileRef.current = isMobile;
    return isMobile;
  }, []);

  useEffect(() => {
    if (id) {
      const listId = parseInt(id);
      if (!isNaN(listId)) {
        loadListDetail(listId);
      }
    }
  }, [id]);

  // 监听pagination.current的变化，触发加载更多数据
  useEffect(() => {
    if (pagination.current > 1 && id) {
      const listId = parseInt(id);
      if (!isNaN(listId)) {
        loadListApps(listId, pagination.current);
      }
    }
  }, [pagination.current, id]);

  // 使用 ref 存储最新状态，避免闭包问题
  const stateRef = useRef({
    isScrollLoading,
    loading,
    apps: apps,
    pagination: pagination,
  });

  // 更新 ref 中的状态
  useEffect(() => {
    stateRef.current = {
      isScrollLoading,
      loading,
      apps: apps,
      pagination: pagination,
    };
  }, [isScrollLoading, loading, apps, pagination]);

  // 滚动加载处理函数
  const handleScroll = useCallback(() => {
    const state = stateRef.current;
    
    // 检查是否已经在加载或没有更多数据
    if (state.isScrollLoading || state.loading) return;
    
    // 检查是否还有更多数据需要加载
    const hasMore = state.apps.length < state.pagination.total;
    if (!hasMore) return;

    let scrollTop, scrollHeight, clientHeight;

    // PC端使用window滚动，移动端设备使用容器滚动
    if (!isMobileDevice()) {
      // PC端：监听window滚动
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    } else {
      // 移动端设备：监听容器滚动
      const scrollContainer = gamesMainRef.current;
      if (!scrollContainer) return;

      scrollTop = scrollContainer.scrollTop;
      scrollHeight = scrollContainer.scrollHeight;
      clientHeight = scrollContainer.clientHeight;
    }

    // 检查是否滚动到底部附近（距离底部100px时开始加载）
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // 检查是否还有更多数据需要加载
      const hasMoreData = state.pagination.current * state.pagination.pageSize < state.pagination.total;
      if (hasMoreData) {
        // 增加页码以触发数据加载
        setPagination(prev => ({ ...prev, current: prev.current + 1 }));
      }
    }
  }, [isMobileDevice]);

  // 节流处理函数
  const throttledHandleScroll = useRef<(() => void) | null>(null);
  const lastScrollTimeRef = useRef<number>(0);
  
  useEffect(() => {
    const throttleDelay = 200;
    
    throttledHandleScroll.current = () => {
      const now = Date.now();
      if (now - lastScrollTimeRef.current < throttleDelay) {
        return;
      }
      lastScrollTimeRef.current = now;
      handleScroll();
    };
  }, [handleScroll]);

  // 滚动加载监听 - 全平台启用
  useEffect(() => {
    const isMobile = isMobileDevice();
    const throttledHandler = throttledHandleScroll.current;
    
    if (!throttledHandler) return;

    // 设置滚动监听器
    if (!isMobile) {
      // PC端：监听window滚动
      window.addEventListener('scroll', throttledHandler, { passive: true });
      
      return () => {
        window.removeEventListener('scroll', throttledHandler);
      };
    } else {
      // 移动端设备：监听容器滚动
      let container: HTMLElement | null = null;
      let timer1: NodeJS.Timeout | null = null;
      let timer2: NodeJS.Timeout | null = null;
      let rafId: number | null = null;

      const setupMobileScroll = () => {
        const currentContainer = gamesMainRef.current;
        if (currentContainer) {
          // 移除旧的监听器（如果存在）
          if (container && container !== currentContainer) {
            container.removeEventListener('scroll', throttledHandler);
          }
          currentContainer.addEventListener('scroll', throttledHandler, { passive: true });
          container = currentContainer;
          return true;
        }
        return false;
      };

      // 立即尝试绑定
      if (gamesMainRef.current) {
        setupMobileScroll();
      } else {
        // 使用 requestAnimationFrame
        rafId = requestAnimationFrame(() => {
          if (!setupMobileScroll()) {
            // 如果第一次失败，延迟重试
            timer1 = setTimeout(() => {
              if (!setupMobileScroll()) {
                // 最后一次重试
                timer2 = setTimeout(() => {
                  setupMobileScroll();
                }, 100);
              }
            }, 50);
          }
        });
      }

      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        if (timer1) clearTimeout(timer1);
        if (timer2) clearTimeout(timer2);
        // 使用最新的 ref 值来移除监听器
        const currentContainer = gamesMainRef.current || container;
        if (currentContainer && throttledHandler) {
          currentContainer.removeEventListener('scroll', throttledHandler);
        }
      };
    }
  }, [isMobileDevice, handleScroll]);

  const loadListDetail = async (listId: number) => {
    try {
      setLoading(true);
      const response = await apiService.getCustomListDetail(listId, {
        page: 1,
        per_page: constants.pagination.defaultPageSize,
      });
      if (response.code === 0) {
        setListDetail(response.data);
        setApps(response.data.apps);
        setPagination({
          current: 1,
          pageSize: constants.pagination.defaultPageSize,
          total: response.data.pagination.total_items,
        });
        setAdPositions([]);
      }
    } catch (error) {
      console.error('Failed to load list detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadListApps = async (listId: number, page: number) => {
    try {
      setIsScrollLoading(true);
      const response = await apiService.getCustomListDetail(listId, {
        page: page,
        per_page: constants.pagination.defaultPageSize,
      });
      if (response.code === 0) {
        // 计算新广告位应该插入的位置（在每页的中间位置）
        const newAdPosition = apps.length + Math.floor(response.data.apps.length / 2);
        setApps(prev => [...prev, ...response.data.apps]);
        setAdPositions(prev => [...prev, newAdPosition]);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total_items,
        }));
      }
    } catch (error) {
      console.error('Failed to load list apps:', error);
    } finally {
      setIsScrollLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    if (value.trim()) {
      window.location.href = `/search/${encodeURIComponent(value.trim())}`;
    }
  };

  // 渲染游戏列表和广告位
  const renderGamesWithAds = () => {
    const items: React.ReactNode[] = [];
    let adIndex = 0;
    
    apps.forEach((app: CustomListAppItem, index) => {
      // 添加游戏卡片
      items.push(
        <Col key={`${app.id}-${index}`} xs={12} sm={8} md={6} lg={4} xl={4}>
          <div
            className="fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <AppCard
              app={app}
              isNew={index < 3}
              isHot={index >= 3 && index < 6}
            />
          </div>
        </Col>
      );
      
      // 检查是否需要在当前位置插入广告位
      if (adPositions.includes(index) && adIndex < adPositions.length) {
        items.push(
          <Col key={`ad-${adIndex}`} xs={24} sm={24} md={24} lg={24} xl={24}>
            <div className="ad-in-list">
              <Ad positionName="custom_list_ad" className="custom-list-ad" />
            </div>
          </Col>
        );
        adIndex++;
      }
    });
    
    return items;
  };

  return (
    <Layout className="list-layout">
      <Header onSearch={handleSearch} />
      <CategoryNav
        selectedCategoryId={0}
      />
      <MobileCategoryNav
        selectedCategoryId={0}
      />

      <Content className="list-content">
        <div className="content-container list-container">
          {/* 自定义列表广告位 */}
          <Ad positionName="custom_list_ad" className="custom-list-ad" />
          
          {/* Main Games List Area */}
          <main className="games-main" ref={gamesMainRef}>
            <Spin spinning={loading && pagination.current === 1}>
              {apps.length > 0 ? (
                <>
                  {listDetail?.show_title && (
                    <h1 className="list-title">{listDetail.name}</h1>
                  )}
                  <Row gutter={[16, 16]} className="games-grid">
                    {renderGamesWithAds()}
                  </Row>
                </>
              ) : (
                <Empty
                  description="No games found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Spin>

            {/* 滚动加载指示器 - 全平台显示 */}
            {isScrollLoading && (
              <div className="scroll-loading-container">
                <Spin size="large" />
                <div className="loading-text">Loading more games...</div>
              </div>
            )}
          </main>
        </div>
      </Content>
      
      <Footer />
    </Layout>
  );
};

export default List;
